// web/src/scene/Canvas.tsx
// M1.5: R3F Canvas wrapper with BVH patching, GPU-tier detection, FPS
// reporting, scene-ready flag, and Redux context bridging into R3F's isolated
// React root.
import React, { useEffect, useRef } from 'react'
import { Canvas as R3FCanvas, useFrame } from '@react-three/fiber'
import { Provider, useStore } from 'react-redux'
import * as THREE from 'three'
import {
  computeBoundsTree,
  disposeBoundsTree,
  acceleratedRaycast,
} from 'three-mesh-bvh'
import { MineScene } from './MineScene'
import { useAppDispatch } from '../app/useAppDispatch'
import { viewerActions } from '@shared/slices/viewerSlice'
import { SceneRefContext, type SceneRef } from './SceneRefContext'

// ---------------------------------------------------------------------------
// BVH patching (idempotent — GLBLoader also patches, but doing it here means
// ground-snap works before any GLB loads in M2 scenarios).
// ---------------------------------------------------------------------------
;(THREE.BufferGeometry.prototype as any).computeBoundsTree = computeBoundsTree
;(THREE.BufferGeometry.prototype as any).disposeBoundsTree = disposeBoundsTree
;(THREE.Mesh.prototype as any).raycast = acceleratedRaycast

// ---------------------------------------------------------------------------
// GPU tier heuristic
// ---------------------------------------------------------------------------
function detectGPUTier(): 'low' | 'high' {
  const cores = navigator.hardwareConcurrency || 2
  return cores >= 5 ? 'high' : 'low'
}

const gpuTier = detectGPUTier()

// ---------------------------------------------------------------------------
// FPSReporter
// Runs inside the R3F render loop; dispatches fps to Redux every 2 s.
// ---------------------------------------------------------------------------
const FPSReporter: React.FC = () => {
  const dispatch = useAppDispatch()
  const frameCount = useRef(0)
  const lastReportTime = useRef(Date.now())

  useFrame(() => {
    frameCount.current += 1
    const now = Date.now()
    if (now - lastReportTime.current >= 2000) {
      const fps = Math.round(
        (frameCount.current * 1000) / (now - lastReportTime.current),
      )
      dispatch(viewerActions.setFps(fps))
      frameCount.current = 0
      lastReportTime.current = now
    }
  })

  return null
}

// ---------------------------------------------------------------------------
// SceneReadyReporter
// Dispatches setSceneReady(true) on mount, false on unmount.
// ---------------------------------------------------------------------------
const SceneReadyReporter: React.FC = () => {
  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(viewerActions.setSceneReady(true))
    return () => {
      dispatch(viewerActions.setSceneReady(false))
    }
  }, [dispatch])
  return null
}

// ---------------------------------------------------------------------------
// CanvasContents
// Rendered inside the R3F Canvas root; receives gpuTier as a prop so closure
// capture is avoided.
// ---------------------------------------------------------------------------
const CanvasContents: React.FC<{ gpuTier: 'low' | 'high' }> = ({ gpuTier }) => (
  <>
    <SceneReadyReporter />
    <FPSReporter />
    <MineScene gpuTier={gpuTier} />
  </>
)

// ---------------------------------------------------------------------------
// SceneCanvas (public export)
// ---------------------------------------------------------------------------

interface SceneCanvasProps {
  sceneRefHolder: React.MutableRefObject<SceneRef | null>
}

export const SceneCanvas: React.FC<SceneCanvasProps> = ({ sceneRefHolder }) => {
  // Capture the outer Redux store so it can be re-provided inside R3F's
  // isolated React root.  R3F 8.x with React 18 may propagate context
  // automatically, but re-providing it here is the robust guarantee.
  const outerStore = useStore()
  const isHighTier = gpuTier === 'high'

  // The outer SceneRefContext.Provider lives in App.tsx so Toolbar (a
  // sibling of SceneCanvas) can call useSceneRef(). Inside the R3F
  // <Canvas> we re-provide Redux store + SceneRefContext because R3F 8.x
  // creates an isolated React root and context propagation is not guaranteed.
  return (
    <R3FCanvas
      shadows={isHighTier}
      dpr={isHighTier ? [1, 1.5] : [1, 1]}
      gl={{
        antialias: isHighTier,
        alpha: false,
        powerPreference: 'high-performance',
        stencil: false,
        depth: true,
      }}
      camera={{
        fov: 60,
        near: 1,
        far: 20000,
        position: [9060, 6049, 6000],
        up: [0, 0, 1],
      }}
      style={{ width: '100%', height: '100%', touchAction: 'none' }}
      onCreated={({ gl }) => {
        gl.setClearColor('#0D0D12')
        gl.localClippingEnabled = true
        if (isHighTier) {
          gl.shadowMap.enabled = true
          gl.shadowMap.type = THREE.PCFSoftShadowMap
          gl.toneMapping = THREE.ACESFilmicToneMapping
          gl.toneMappingExposure = 1.8
        } else {
          gl.shadowMap.enabled = false
          gl.toneMapping = THREE.NoToneMapping
        }
      }}
    >
      <Provider store={outerStore}>
        <SceneRefContext.Provider value={sceneRefHolder}>
          <CanvasContents gpuTier={gpuTier} />
        </SceneRefContext.Provider>
      </Provider>
    </R3FCanvas>
  )
}
